import { get } from 'svelte/store';
import { cloud, handle_base_url, handleToken, standardLogin } from '../http';
// stores
import { user_data } from 'src/stores/account/user';
import {
	device_auths,
	req_locked_devices,
	auth_locked_devices,
	auth_unlocked_at,
} from 'src/stores/device-runtime/local-auth';
import {
	firmwares_gen_2,
	updating_devices,
	wifi_updating_devices,
} from 'src/stores/device-runtime/process';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { deviceRuntimeResource } from 'src/stores/device-runtime/resource-state';
import { devices } from 'src/stores/lists/devices';
import { app } from 'src/stores/session/native-bridge';
import { rebooting_devices } from 'src/stores/device-runtime/process';
import {
	cachedLists,
	cloud_disabled_devices,
	pending_connection_devices,
	authLockedDeviceModal,
	unauthorisedRequests,
} from 'src/stores/device-runtime/firmware-ui';

// helpers
import {
	is_gen_2,
	isCamera,
	isWallDisplay,
	is_loqed,
	isXMODDevice,
	processXMODStatus,
	real_id,
	get_local_ip,
} from 'src/helpers/interface_helpers';
import { handleCameraStatusUpdate } from 'src/components/Camera/helpers/camera-events';
import type { CameraDeviceStatus } from 'src/components/Camera/types/camera-status';
import { removeUpdatingState } from 'src/helpers/firmware_helpers';
import { checkMode } from 'src/helpers/mode_switch_helpers';
import { mobile } from '../mobile-rework';
import { online_devices_ios } from '../native_container_requests_ios';
import { online_devices_android } from '../native_container_requests_android';

// types
import type { Device } from 'src/types/Device';
import type { GetDevices } from 'src/types/requests/iotu/GetDevices';
import { loqedPendingOperationsDevices } from 'src/stores/integrations/loqed';
import type { DeviceState, getDevicesStateResponse } from 'src/types/responses/iotuDeviceState';
import { updateWallDisplayChannels } from 'src/helpers/wall_display_extract/extracting_helpers';
import { updateXMODChannels } from 'src/helpers/xmod_extract/extracting_helpers';
import { handleLoginErrors } from 'src/helpers/account_helpers';
import { trigger401ToGetAuthHeader } from '../local_requests';
import { HALF_SLEEPING_TYPES, SLEEPING_TYPES } from 'src/constants';
import { isFwAfter200 } from 'src/helpers/ble_pairing_helpers';
import { getObserverLists } from 'src/helpers/observer_lists_helper';

export async function getDevicesState(): Promise<getDevicesStateResponse | undefined> {
	const generation = deviceRuntimeResource.begin();
	const reqParams: GetDevices = {
		select: ['status', 'settings'],
		show: ['offline', 'shared'],
	};

	try {
		const res = await fetchDeviceState(reqParams);
		if (res?.status !== 200) {
			throw new Error(`Failed to fetch devices state (${res?.status ?? 'unknown status'})`);
		}

		const data: getDevicesStateResponse = res.data;
		for (let item of data) {
			try {
				await processDevice(item);
			} catch (err) {
				console.error('Error processing device state: ', item, err);
			}
		}

		if (!deviceRuntimeResource.isCurrent(generation)) {
			return data;
		}

		await updateDevicesStatus(data);
		updateDevicesSettings(data);
		updateMobileDeviceCount(data);
		deviceRuntimeResource.complete(generation);

		setTimeout(() => auth_unlocked_at.recheckExpiredAndReLock(), 0);

		return data;
	} catch (error) {
		deviceRuntimeResource.fail(generation, error);
		console.error('Error fetching devices state', error);
		return undefined;
	}
}

export async function getDevicesCache(): Promise<getDevicesStateResponse> {
	const reqParams: GetDevices = {
		select: ['cache'],
		show: ['offline'],
	};

	const res = await fetchDeviceState(reqParams);
	if (res?.status === 200) {
		updateCachedLists(res.data);
		return res.data;
	} else {
		console.error('Error fetching devices cache', res?.status);
	}
}

export const getDevicesStateByID = async (id: string) => {
	const reqParams: GetDevices = {
		ids: [id],
		select: ['status', 'settings', 'cache'],
		show: ['offline', 'shared'],
	};
	const res = await fetchDeviceState(reqParams);
	if (res?.status === 200) {
		try {
			await processDevice(res.data[0]);
		} catch (err) {
			console.error('Error processing device state: ', res?.data[0], err);
		}

		const cloudStatusCache = devices_status.getByRealId(res.data[0].id);
		if (res.data[0]?.status) {
			devices_status.update_device_status({
				id: res.data[0].id,
				msg:
					cloudStatusCache?._updated > res.data[0]?.status?._updated
						? { ...res.data[0]?.status, ...cloudStatusCache }
						: { ...cloudStatusCache, ...res.data[0]?.status },
			});
		}
		if (res.data[0]?.settings) {
			devices_settings.update_devices_settings(res.data[0].id, res.data[0].settings);
		}
		const data = Object.values(get(devices_status)).map((status) => ({
			status,
		})) as getDevicesStateResponse;
		updateMobileDeviceCount(data);
	} else {
		console.error('Error while fetching the device by id', res);
	}
};

// Fetch device state from the IOTU v2 API
async function fetchDeviceState(reqParams: GetDevices) {
	try {
		return await cloud.post('/v2/devices/get', reqParams);
	} catch (error) {
		if (error.response?.data?.error === 'WRONG_INSTANCE') {
			const res = await standardLogin(get(user_data).email, get(user_data).password);

			if (res?.isok) {
				return await cloud.post('/v2/devices/get', reqParams);
			} else {
				const alertProps = handleLoginErrors(res);
				console.error(JSON.stringify(alertProps));
			}
		} else {
			console.error(
				'Error while fetching the device state from the IOTU v2 API: ',
				error.response,
			);
		}
	}
}

// Process each device in the response
async function processDevice(deviceData: DeviceState) {
	const id = deviceData.id;
	const realId = real_id(id);
	const device: Device = get(devices)[id];

	if (!deviceData.status) {
		return;
	}
	deviceData.status.online = deviceData.online;
	if (deviceData?.sleeping) {
		updateDeviceSleeping(deviceData);
	}
	checkMode(id, deviceData?.settings, true);
	updateDeviceTimestamp(deviceData);
	removeDeviceFromLists(id, deviceData, device);
	handleWifiUpdatingDevices(id);
	if (isWallDisplay(deviceData?.code) && typeof device !== 'undefined') {
		updateWallDisplayChannels(id, deviceData?.settings);
	}
	if (isXMODDevice(deviceData?.code) && typeof device !== 'undefined') {
		updateXMODChannels(id, deviceData?.settings, deviceData?.status);
	}
	const authEnabled = deviceData?.settings?.DeviceInfo?.auth_en;
	const isOnline = deviceData?.online === 1;
	const devicesNotAllowedToUseLocal =
		SLEEPING_TYPES.includes(deviceData?.code) || HALF_SLEEPING_TYPES.includes(deviceData?.code);
	if (devicesNotAllowedToUseLocal) {
		auth_locked_devices.remove(realId);
		return;
	}
	if (authEnabled === false) {
		device_auths.remove(realId);
		auth_locked_devices.remove(realId);
	} else if (!isOnline && authEnabled === true) {
		auth_locked_devices.remove(realId);
	} else if (authEnabled === true && get(app)) {
		const hasCredentials =
			typeof get(device_auths)[realId] !== 'undefined' && get(device_auths)[realId] !== null;
		if (hasCredentials) return;

		if (!isOnline) return;

		const device = get(devices)[id];
		const local_ip = get_local_ip(device?.id);

		if (local_ip && device) {
			auth_locked_devices.add(realId);

			if (is_gen_2(device)) {
				trigger401ToGetAuthHeader(device, local_ip);
			} else {
				const baseUrl = local_ip.substring(0, 4) !== 'http' ? `http://${local_ip}` : local_ip;
				const url = `${baseUrl}/settings`;
				unauthorisedRequests.update((reqs) => {
					return {
						...reqs,
						[realId]: {
							responseHeaders: null,
							payload: {},
							url: url,
						},
					};
				});
			}
		}
	}
}
//Update _sleeping state
function updateDeviceSleeping(deviceData: DeviceState) {
	//prop is sleeping and value is 1 so adapting it to our status's state
	if (deviceData?.sleeping) {
		deviceData.status._sleeping = true;
	}
}

// Update the _updated timestamp
function updateDeviceTimestamp(deviceData: DeviceState) {
	let updated = deviceData?.status?._updated;
	if (updated) {
		updated = updated.replace(' ', 'T') + 'Z';
		deviceData.status._updated = +(new Date(updated).getTime() / 1000).toFixed(0);
	}
}

// Remove device from various lists based on conditions
function removeDeviceFromLists(id: string, deviceData: DeviceState, device: Device) {
	if (get(rebooting_devices).indexOf(id) !== -1) {
		rebooting_devices.remove(id);
	}

	const updatingDevice = get(updating_devices)[id];
	if (updatingDevice && !is_gen_2(device) && deviceData?.status.update?.status !== 'updating') {
		removeUpdatingState(device);
	}

	if (updatingDevice && shouldRemoveUpdatingDevice(updatingDevice, deviceData)) {
		removeUpdatingState(device);
		firmwares_gen_2.remove(id);
	}

	if (is_loqed(device)) {
		// remove the id from the Loqed pending operations list
		if (typeof get(loqedPendingOperationsDevices)[id] !== 'undefined') {
			loqedPendingOperationsDevices.remove(id);
		}
	}

	removeFromPendingAndLockedDevices(id);
}

// Determine if the updating device should be removed
function shouldRemoveUpdatingDevice(updatingDevice: any, deviceData: DeviceState): boolean {
	return (
		(deviceData?.settings?.DeviceInfo &&
			deviceData?.settings?.DeviceInfo?.fw_id === updatingDevice.fw) ||
		Date.now() > updatingDevice?.ts + 2 * 60 * 1000 ||
		typeof updatingDevice.ts === 'undefined'
	);
}

// Remove device from pending and locked devices
function removeFromPendingAndLockedDevices(id: string) {
	if (get(pending_connection_devices).indexOf(id) > -1) {
		pending_connection_devices.remove(id);
	}
	if (get(req_locked_devices).indexOf(id) !== -1) {
		req_locked_devices.remove(id);
	}
}

// Handle wifi updating devices
function handleWifiUpdatingDevices(id: string) {
	if (get(wifi_updating_devices).some((updating_data) => Object.keys(updating_data)[0] === id)) {
		const { channel, desired_wifi } = get(wifi_updating_devices).find(
			(updating_data) => Object.keys(updating_data)[0] === id,
		);
		if (isDeviceConnectedToDesiredWifi(desired_wifi, channel, id)) {
			wifi_updating_devices.remove(id);
		}
	}
}

// Check if the device is connected to the desired wifi
function isDeviceConnectedToDesiredWifi(ssid: string, sta: number, i: string): boolean {
	return (
		ssid === devices_settings.getByRealId(i)?.[sta === 0 ? 'wifi_sta' : 'wifi_sta1']?.ssid ||
		ssid === devices_settings.getByRealId(i)?.wifi[sta === 0 ? 'sta' : 'sta1']?.ssid
	);
}

// Update the devices status in the store
async function updateDevicesStatus(data: getDevicesStateResponse) {
	const cloudDisabledStatusCache = cacheCloudDisabledStatuses();
	const devicesStatus = await buildDevicesStatus(data);

	devices_status.hydrate({
		...get(devices_status),
		...devicesStatus,
		...cloudDisabledStatusCache,
		...mergeCloudAndDeviceStatuses(devicesStatus, cloudDisabledStatusCache),
	});

	notifyCameraStatuses(data);
}

/**
 * Push a camera status that was just written to its camera-specific consumers, the
 * same way the socket path does. Each caller passes the status it stores.
 */
function notifyCameraStatus(item: DeviceState, status: CameraDeviceStatus = item?.status) {
	if (status && isCamera(item?.code)) {
		handleCameraStatusUpdate(item.id, status);
	}
}

function notifyCameraStatuses(data: getDevicesStateResponse) {
	for (const item of data) {
		notifyCameraStatus(item);
	}
}

// Cache the cloud-disabled statuses
function cacheCloudDisabledStatuses() {
	const cloudDisabledStatusCache = {};
	Object.keys(get(cloud_disabled_devices)).forEach((dId) => {
		cloudDisabledStatusCache[dId] = devices_status.getByRealId(dId);
	});
	return cloudDisabledStatusCache;
}

// Build the devices status from the response data
async function buildDevicesStatus(data: getDevicesStateResponse) {
	const acc: Record<string, DeviceState> = {};
	for (const item of data) {
		if (item?.status) {
			const device = get(devices)[item.id];
			if (isXMODDevice(device?.type)) {
				acc[item.id] = await processXMODStatus(device, item.status);
			} else {
				acc[item.id] = item.status;
			}
		}
	}
	return acc;
}

// Merge cloud and device statuses
function mergeCloudAndDeviceStatuses(devicesStatus: any, cloudDisabledStatusCache: any) {
	return Object.keys(cloudDisabledStatusCache).reduce((acc, deviceId) => {
		if (
			!devicesStatus[deviceId] ||
			cloudDisabledStatusCache[deviceId]?._updated > devicesStatus[deviceId]?._updated
		) {
			acc[deviceId] = {
				...devicesStatus[deviceId],
				...cloudDisabledStatusCache[deviceId],
			};
		} else {
			acc[deviceId] = {
				...cloudDisabledStatusCache[deviceId],
				...devicesStatus[deviceId],
			};
		}
		return acc;
	}, {});
}

// Update the devices settings in the store.
// Preserves cloud-disabled device settings (not in v2 response) by merging with existing.
function updateDevicesSettings(data: getDevicesStateResponse) {
	const devicesSettings = data.reduce(
		(acc, device) => {
			if (device?.settings) {
				acc[device.id] = device.settings;
			}
			return acc;
		},
		{} as Record<string, DeviceState>,
	);

	const existingSettings = get(devices_settings);
	const cloudDisabledIds = Object.keys(get(cloud_disabled_devices));
	const cloudDisabledSettings = cloudDisabledIds.reduce(
		(acc, dId) => {
			const s = existingSettings[dId];
			if (s) acc[dId] = s;
			return acc;
		},
		{} as Record<string, DeviceState>,
	);

	devices_settings.hydrate({
		...devicesSettings,
		...cloudDisabledSettings,
	});
}

// Update the count of online devices for mobile platforms
function updateMobileDeviceCount(data: getDevicesStateResponse) {
	const newData = data.filter((device) => device?.status?.online);
	if (get(app) && mobile.isMobile) {
		if (mobile.OS === 'IOS') {
			online_devices_ios(newData.length);
		} else {
			online_devices_android(newData.length);
		}
	}
}

//#region Lists Cache
// Update the lists cache
function updateCachedLists(data: getDevicesStateResponse) {
	const devicesCache = data.reduce(
		(acc, device) => {
			if (device?.cache) {
				acc[device.id] = device.cache;
			}
			return acc;
		},
		{} as Record<string, DeviceState>,
	);

	cachedLists.set({
		...devicesCache,
	});
}

/**
 * FW 2.0.0+ devices may not have their cloud relay list in the cloud cache
 * because the observer config was removed. Fetch directly from online devices.
 */
export const backfillCloudRelayLists = async () => {
	const _devices = get(devices);
	const _settings = get(devices_settings);
	const _status = get(devices_status);
	const _cached = get(cachedLists);

	const gatewayDevices = Object.values(_devices).filter((dev: Device) => {
		const rid = real_id(dev.id);
		return (
			is_gen_2(dev) &&
			!dev.shared &&
			dev.channel == 0 &&
			_status?.[rid]?.online &&
			isFwAfter200(_settings?.[rid]) &&
			!_cached?.[rid]?.['ble.cloudrelay.list']?.addrs?.length &&
			!SLEEPING_TYPES.includes(dev.type)
		);
	});

	if (gatewayDevices.length === 0) return;

	const results = await Promise.allSettled(
		gatewayDevices.map(async (dev: Device) => {
			const rid = real_id(dev.id);
			const res = await getObserverLists(dev);
			if (res?.isok && res?.data?.addrs) {
				return { id: rid, addrs: res.data.addrs };
			}
			return null;
		}),
	);

	const updates: Record<string, any> = {};
	for (const result of results) {
		if (result.status === 'fulfilled' && result.value) {
			updates[result.value.id] = {
				...(_cached[result.value.id] || {}),
				'ble.cloudrelay.list': { addrs: result.value.addrs },
			};
		}
	}

	if (Object.keys(updates).length > 0) {
		cachedLists.update((lists) => ({ ...lists, ...updates }));
	}
};

//#endregion Lists Cache
